import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Ban, CheckCircle2, Loader2, Pencil, Plus, RefreshCw, Tags } from 'lucide-react';
import { btnPrimary, btnSecondary, card, errorText, hint, input, label, sectionTitle } from '../dietitian/ui';
import { makeT, useLanguage } from '../../hooks/useLanguage';
import { PricingPresets } from '../../services/dietitianApi';
import { presetIdFromName, presetName, priceWording, trialWording } from '../../utils/dietitianInvite';

const strings = {
  english: {
    title: 'Plans',
    subtitle: 'Price and trial presets coaches pick for their clients.',
    newPlan: 'New plan',
    loading: 'Loading…',
    loadError: 'Could not load plans.',
    retry: 'Try again',
    empty: 'No plans yet',
    emptyHint: 'Add the first plan so coaches can pick it.',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',
    id: 'Plan id',
    idHint: 'Lowercase letters, numbers and _. Cannot change later.',
    nameEn: 'Name (English)',
    nameHe: 'Name (Hebrew)',
    monthly: 'Monthly price ($)',
    yearly: 'Yearly price ($)',
    priceNote: 'Changing a price applies to new clients; current subscribers keep their price.',
    trialType: 'Trial',
    trial_free_month: '1 free month, no card',
    trial_card_trial: 'Trial with card',
    trial_none: 'No trial',
    trialDays: 'Trial days',
    sort: 'Order',
    isActive: 'Coaches can pick this plan',
    save: 'Save plan',
    create: 'Create plan',
    saving: 'Saving…',
    cancel: 'Cancel',
    saved: 'Plan saved',
    created: 'Plan created',
    nothing: 'No changes to save.',
    error: 'Could not save: {msg}',
    'err.id': 'Use 2–40 lowercase letters, numbers or _.',
    'err.name': 'Required.',
    'err.price': 'Between 1 and 1000.',
    'err.days': 'Between 1 and 60.',
  },
  hebrew: {
    title: 'מסלולים',
    subtitle: 'מחירים ותקופות ניסיון שמאמנים בוחרים ללקוחות שלהם.',
    newPlan: 'מסלול חדש',
    loading: 'טוען…',
    loadError: 'לא הצלחנו לטעון את המסלולים.',
    retry: 'נסו שוב',
    empty: 'אין מסלולים עדיין',
    emptyHint: 'הוסיפו מסלול ראשון כדי שמאמנים יוכלו לבחור בו.',
    active: 'פעיל',
    inactive: 'לא פעיל',
    edit: 'עריכה',
    id: 'מזהה מסלול',
    idHint: 'אותיות קטנות באנגלית, ספרות ו-_. אי אפשר לשנות אחר כך.',
    nameEn: 'שם (אנגלית)',
    nameHe: 'שם (עברית)',
    monthly: 'מחיר חודשי ($)',
    yearly: 'מחיר שנתי ($)',
    priceNote: 'שינוי מחיר חל על לקוחות חדשים; מנויים קיימים שומרים על המחיר שלהם.',
    trialType: 'תקופת ניסיון',
    trial_free_month: 'חודש ראשון חינם, בלי כרטיס',
    trial_card_trial: 'ניסיון עם כרטיס אשראי',
    trial_none: 'ללא ניסיון',
    trialDays: 'ימי ניסיון',
    sort: 'סדר',
    isActive: 'מאמנים יכולים לבחור במסלול',
    save: 'שמירת מסלול',
    create: 'יצירת מסלול',
    saving: 'שומר…',
    cancel: 'ביטול',
    saved: 'המסלול נשמר',
    created: 'המסלול נוצר',
    nothing: 'אין שינויים לשמירה.',
    error: 'השמירה נכשלה: {msg}',
    'err.id': '2–40 אותיות קטנות באנגלית, ספרות או _.',
    'err.name': 'שדה חובה.',
    'err.price': 'בין 1 ל-1000.',
    'err.days': 'בין 1 ל-60.',
  },
};

const TRIAL_TYPES = ['free_month', 'card_trial', 'none'];
const blank = { id: '', name_en: '', name_he: '', monthly_dollars: '', yearly_dollars: '', trial_type: 'card_trial', trial_days: '3', sort: '0', is_active: true };

const toForm = (p) => ({
  id: p.id,
  name_en: p.name_en || '',
  name_he: p.name_he || '',
  monthly_dollars: p.monthly_dollars != null ? String(p.monthly_dollars) : '',
  yearly_dollars: p.yearly_dollars != null ? String(p.yearly_dollars) : '',
  trial_type: p.trial_type || 'none',
  trial_days: String(p.trial_days ?? 0),
  sort: String(p.sort ?? 0),
  is_active: p.is_active !== false,
});

const toPayload = (f) => ({
  name_en: f.name_en.trim(),
  name_he: f.name_he.trim(),
  monthly_dollars: Number(f.monthly_dollars),
  yearly_dollars: Number(f.yearly_dollars),
  trial_type: f.trial_type,
  trial_days: f.trial_type === 'card_trial' ? Number(f.trial_days) : Number(f.trial_days) || 0,
  sort: Number(f.sort) || 0,
  is_active: !!f.is_active,
});

function validate(f, isNew) {
  const e = {};
  if (isNew && !/^[a-z0-9_]{2,40}$/.test(f.id)) e.id = 'err.id';
  if (!f.name_en.trim()) e.name_en = 'err.name';
  if (!f.name_he.trim()) e.name_he = 'err.name';
  for (const k of ['monthly_dollars', 'yearly_dollars']) {
    const n = Number(f[k]);
    if (f[k] === '' || !Number.isFinite(n) || n < 1 || n > 1000) e[k] = 'err.price';
  }
  if (f.trial_type === 'card_trial') {
    const d = Number(f.trial_days);
    if (!Number.isInteger(d) || d < 1 || d > 60) e.trial_days = 'err.days';
  }
  return e;
}

function PresetForm({ initial, isNew, onCancel, onSaved, t }) {
  const [form, setForm] = useState(initial);
  const [idTouched, setIdTouched] = useState(!isNew);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const prefix = isNew ? 'preset-new' : `preset-${initial.id}`;

  const set = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (isNew && key === 'name_en' && !idTouched) next.id = presetIdFromName(value);
      return next;
    });
    setErrors((e) => ({ ...e, [key]: undefined }));
    setFormError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form, isNew);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const payload = toPayload(form);
    let body = payload;
    if (!isNew) {
      const before = toPayload(initial);
      body = Object.fromEntries(Object.entries(payload).filter(([k, v]) => before[k] !== v));
      if (!Object.keys(body).length) { toast(t('nothing')); return; }
    }
    setBusy(true);
    try {
      const saved = isNew ? await PricingPresets.create({ id: form.id, ...payload }) : await PricingPresets.update(initial.id, body);
      toast.success(isNew ? t('created') : t('saved'));
      onSaved(saved);
    } catch (err) {
      setFormError(t('error', { msg: err.message || '' }));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, labelKey, props = {}) => (
    <div>
      <label htmlFor={`${prefix}-${key}`} className={label}>{t(labelKey)}</label>
      <input id={`${prefix}-${key}`} className={input} value={form[key]} aria-invalid={errors[key] ? 'true' : undefined}
        onChange={(e) => set(key, e.target.value)} {...props} />
      {errors[key] ? <p className={`${errorText} mt-1`}>{t(errors[key])}</p> : null}
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-4 rounded-lg border border-line bg-canvas p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {field('name_en', 'nameEn', { maxLength: 80 })}
        {field('name_he', 'nameHe', { maxLength: 80, dir: 'rtl' })}
        {isNew ? (
          <div className="sm:col-span-2">
            <label htmlFor={`${prefix}-id`} className={label}>{t('id')}</label>
            <input id={`${prefix}-id`} dir="ltr" className={`${input} font-mono`} value={form.id}
              aria-invalid={errors.id ? 'true' : undefined}
              onChange={(e) => { setIdTouched(true); set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 40)); }} />
            {errors.id ? <p className={`${errorText} mt-1`}>{t(errors.id)}</p> : <p className={`${hint} mt-1`}>{t('idHint')}</p>}
          </div>
        ) : null}
        {field('monthly_dollars', 'monthly', { type: 'number', inputMode: 'decimal', min: 1, max: 1000, step: '0.01', dir: 'ltr' })}
        {field('yearly_dollars', 'yearly', { type: 'number', inputMode: 'decimal', min: 1, max: 1000, step: '0.01', dir: 'ltr' })}
        <p className={`${hint} sm:col-span-2 -mt-2`}>{t('priceNote')}</p>
        <div>
          <label htmlFor={`${prefix}-trial`} className={label}>{t('trialType')}</label>
          <select id={`${prefix}-trial`} className={input} value={form.trial_type} onChange={(e) => set('trial_type', e.target.value)}>
            {TRIAL_TYPES.map((type) => <option key={type} value={type}>{t(`trial_${type}`)}</option>)}
          </select>
        </div>
        {form.trial_type === 'card_trial' ? field('trial_days', 'trialDays', { type: 'number', inputMode: 'numeric', min: 1, max: 60, dir: 'ltr' }) : <div className="hidden sm:block" />}
        {field('sort', 'sort', { type: 'number', inputMode: 'numeric', dir: 'ltr' })}
        <label className="flex min-h-[44px] items-center gap-2 self-end text-[13px] text-ink">
          <input type="checkbox" className="h-4 w-4 accent-[color:var(--forest-ondark)]" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
          {t('isActive')}
        </label>
      </div>
      {formError ? <p className={errorText} role="alert">{formError}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={btnPrimary} disabled={busy}>
          {busy ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden /> : null}
          {busy ? t('saving') : isNew ? t('create') : t('save')}
        </button>
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>{t('cancel')}</button>
      </div>
    </form>
  );
}

/** sys_admin: list, edit and add pricing presets. */
export default function PlanPresetsAdmin() {
  const { language } = useLanguage();
  const t = makeT(strings, language);
  const [presets, setPresets] = useState([]);
  const [status, setStatus] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [editingId, setEditingId] = useState(null); // preset id, '__new', or null

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const list = await PricingPresets.list({ all: true });
      setPresets(Array.isArray(list) ? list : []);
      setStatus('ready');
    } catch (err) {
      setLoadError(err.message || '');
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setEditingId(null); load(); };

  return (
    <section className={`${card} p-4 sm:p-6`} aria-labelledby="plans-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="plans-title" className={`${sectionTitle} flex items-center gap-2`}>
            <Tags size={18} className="text-forest-ondark" aria-hidden />
            {t('title')}
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">{t('subtitle')}</p>
        </div>
        {editingId !== '__new' && status === 'ready' ? (
          <button type="button" className={btnPrimary} onClick={() => setEditingId('__new')}>
            <Plus size={16} aria-hidden /> {t('newPlan')}
          </button>
        ) : null}
      </div>

      <div className="mt-4 space-y-3">
        {editingId === '__new' ? (
          <PresetForm initial={blank} isNew onCancel={() => setEditingId(null)} onSaved={onSaved} t={t} />
        ) : null}

        {status === 'loading' ? (
          <p className="flex items-center gap-2 py-6 text-[13px] text-ink-muted" role="status">
            <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden /> {t('loading')}
          </p>
        ) : status === 'error' ? (
          <div className="rounded-lg border border-line p-4 text-center" role="alert">
            <AlertCircle size={20} className="mx-auto text-[color:var(--danger-text)]" aria-hidden />
            <p className="mt-1 text-[13px] text-ink">{t('loadError')}</p>
            {loadError ? <p className={hint}>{loadError}</p> : null}
            <button type="button" className={`${btnSecondary} mt-3`} onClick={load}><RefreshCw size={16} aria-hidden /> {t('retry')}</button>
          </div>
        ) : presets.length === 0 && editingId !== '__new' ? (
          <div className="rounded-lg border border-dashed border-line p-6 text-center">
            <p className="text-[13px] font-medium text-ink">{t('empty')}</p>
            <p className={`${hint} mt-1`}>{t('emptyHint')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {presets.map((p) => (
              <li key={p.id} className="p-3">
                {editingId === p.id ? (
                  <PresetForm initial={toForm(p)} onCancel={() => setEditingId(null)} onSaved={onSaved} t={t} />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-ink">
                        {presetName(p, language)} <span className="font-mono text-[11px] text-ink-muted" dir="ltr">{p.id}</span>
                      </p>
                      <p className="text-[13px] text-ink tabular-nums" dir="ltr">{priceWording(p, 'english')}</p>
                      <p className={hint}>{trialWording(p, language)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.is_active !== false ? (
                        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium bg-forest-faint text-forest-ondark">
                          <CheckCircle2 size={12} aria-hidden /> {t('active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium bg-moss text-[color:var(--danger-text)]">
                          <Ban size={12} aria-hidden /> {t('inactive')}
                        </span>
                      )}
                      <button type="button" className={btnSecondary} onClick={() => setEditingId(p.id)} aria-label={`${t('edit')}: ${presetName(p, language)}`}>
                        <Pencil size={16} aria-hidden /> {t('edit')}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
